import {Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpHeaders} from "@angular/common/http";
import {Order} from "../model/order";
import {Observable} from "rxjs";
import {Property} from "../model/property";
import {Cart} from "../model/cart";
import 'rxjs/add/observable/of';
import { LoginIdService } from "./login-id.service";
import {OrderStatistics} from "../model/order-statistics";
import {NgxIndexedDBService} from "ngx-indexed-db";
import {Router} from "@angular/router";
import {MatDialog} from "@angular/material/dialog";
import {tap} from "rxjs/operators";
import {InformationDialogComponent} from "../component/dialogs/information-dialog/information-dialog.component";

@Injectable({
  providedIn: 'root'
})
export class OrderService {

  options = {};
  constructor(private httpClient: HttpClient, private loginIdService: LoginIdService, private indexDBService: NgxIndexedDBService, private router: Router, private dialog: MatDialog) {
    this.loginIdService.headersChanged$.subscribe(headers => {
      this.options = headers;
    });
  }

  getOrderStatistics(orderId: string, options = {}): Observable<OrderStatistics>{
      return this.httpClient.get<OrderStatistics>("/api/orders/" + orderId + "/statistics", options);
  }

  /**
   * Retrieves and order by its id using the 'childlessView' view.
   * Handles occurring errors using the handleError function.
   *
   * @param orderId Id of the order that should be retrieved
   * @param options Http headers to be included in the request
   * @return Observable emitting the retrieved order
   */
  getChildlessOrderById(orderId: string, options : {} = {}): Observable<Order>{
      return this.httpClient.get<Order>("/api/orders/" +orderId + "?view=childlessView", options).pipe(tap({
        error: (err) => {
          this.handleError(err);
        }
      }));
  }

  getMultiUserOrders(archived: boolean = false, options = {}): Observable<Order[]> {
    const url = `/api/multi-user-orders${archived ? '?archived=true' : ''}`;
    return this.httpClient.get<Order[]>(url, options);
  }

  getOrderById(orderId, orders: Order[] = [], options = {}): Observable<Order> {
    let searchedOrder;
    if (orders)
      orders.forEach(order => {
        if (orderId === order.orderid && order.properties.length > 0)
          searchedOrder = order;
      });
    if (!searchedOrder)
      return this.httpClient.get<Order>("/api/orders/" + orderId, options);
    else
      return Observable.of(searchedOrder);
  }

  /**
   * Retrieves a detailed payment summary for the given list of properties.
   *
   * @param properties List of property objects used to calculate pricing.
   * @returns          An {@link Observable} emitting the payment summary data from the backend.
   */
  getPaymentSummary(properties: Property[]): Observable<any> {
    return this.httpClient.post("/api/orders/pricing-preview", properties, this.options);
  }

  /*
  returns all authorities from the indexedDB
   */
  getFiles(): Promise<any[]> {
    return this.indexDBService.getAll('authorities').toPromise();
  }

  async setMultiUserOrder(cart: Cart): Promise<Observable<any>> {
    let httpOptions = {
      responseType: 'text' as 'json',
      headers: this.loginIdService.getCurrentHeaders()
    }
    const formData = new FormData();

    await this.getFiles().then(files => {
      files.forEach(fileRecord => {
        const renamedFile = new File([fileRecord.file], fileRecord.propertyHash);
        formData.append('files', renamedFile);
      });
    });

    formData.append('cart', new Blob([JSON.stringify(cart)], { type: 'application/json' }));

    return this.httpClient.post("/api/multi-user-orders", formData, httpOptions);
  }

  setOrderArchivedFlag(orderId: string, archived: boolean): Observable<any> {
    return this.httpClient.put(`/api/orders/${orderId}/archive?archived=${archived}`, {}, {headers: new HttpHeaders({'showing-spinner': ''})});
  }

  /**
   * Handles HTTP errors by displaying and appropriate information dialog.
   * Currently only handles errors with the status codes 400 and 403
   *
   * @param error HttpErrorResponse object that contains details about the error
   */
  private handleError(error: HttpErrorResponse): void {
    if (error.status === 400) {
      this.openDeletedOrderInformationDialog();
    }
    else if (error.status === 403) {
      this.openNoAccessOrderInformationDialog();
    }
  }

  /**
   * Opens an information dialog informing the user that the order he is trying to access is already deleted
   */
  private openDeletedOrderInformationDialog(): void{
    this.dialog.open(InformationDialogComponent, {
      width: 'auto',
      data:{
        title: 'Bestellung existiert nicht mehr',
        content: 'Ihre Bestellung wurde bereits gelöscht und kann nicht mehr abgerufen werden.',
        buttonText: 'Zurück zur Bestellübersicht',
        callback: () => {
          this.router.navigate(['/orders']);
        }
      }
    });
  }

  /**
   * Opens an information dialog informing the user that he has no access for the order he is trying to access
   */
  private openNoAccessOrderInformationDialog(): void{
    this.dialog.open(InformationDialogComponent, {
      width: 'auto',
      data:{
        title: 'Sie haben keinen Zugriff',
        content: 'Sie haben keinen Zugriff auf die Bestellung. Kontaktieren Sie Ihren Administrator, um Zugriff zu erhalten.',
        buttonText: 'Zurück zur Bestellübersicht',
        callback: () => {
          this.router.navigate(['/orders']);
        }
      }
    });
  }
}
